<template>
  <section class="media-workspace" :class="hasVideo ? 'is-video' : 'is-audio'">
    <div v-if="hasVideo" class="media-stage">
      <div ref="videoFrame" class="video-frame">
        <YouTubePlayerFrame
          v-if="youtubeVideoId"
          :video-id="youtubeVideoId"
          @media-ready="$emit('media-ready', $event)"
          @media-detached="$emit('media-detached', $event)"
          @media-error="$emit('media-error', $event)"
        />

        <video
          v-else
          ref="videoElement"
          class="video-element"
          :src="videoSrc"
          :poster="posterSrc"
          playsinline
          preload="metadata"
          tabindex="0"
          @click="$emit('toggle-play')"
          @dblclick="toggleFullscreen"
          @keydown.space.stop.prevent="$emit('toggle-play')"
          @keydown.enter.stop.prevent="$emit('toggle-play')"
          @error="$emit('media-error', $event)"
        ></video>

        <button
          v-if="youtubeVideoId"
          type="button"
          class="youtube-video-hit-area"
          :aria-label="isPlaying ? $t('audioPlayer.pauseVideo') : $t('audioPlayer.playVideo')"
          @mousedown.prevent
          @click.stop="$emit('toggle-play')"
          @dblclick.stop="toggleFullscreen"
          @keydown.space.stop.prevent="$emit('toggle-play')"
          @keydown.enter.stop.prevent="$emit('toggle-play')"
        >
        </button>

        <button
          v-if="!youtubeVideoId && !isPlaying"
          type="button"
          class="stage-play-button"
          :aria-label="$t('audioPlayer.playVideo')"
          @click.stop="$emit('toggle-play')"
        >
          <span class="stage-play-icon" aria-hidden="true"></span>
          <span>{{ $t('audioPlayer.playSegment') }}</span>
        </button>

        <span v-if="subtitleCount" class="segment-counter">
          {{ String(subtitleIndex + 1).padStart(2, '0') }} / {{ String(subtitleCount).padStart(2, '0') }}
        </span>

      </div>
    </div>

    <div class="transcript-pane">
      <div class="transcript-body">
        <slot name="transcript"></slot>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import YouTubePlayerFrame from './YouTubePlayerFrame.vue'

export default {
  name: 'PlayerMediaWorkspace',
  components: { YouTubePlayerFrame },
  props: {
    videoSrc: { type: String, default: '' },
    youtubeVideoId: { type: String, default: '' },
    posterSrc: { type: String, default: '' },
    isPlaying: { type: Boolean, default: false },
    subtitleIndex: { type: Number, default: 0 },
    subtitleCount: { type: Number, default: 0 },
  },
  emits: ['media-ready', 'media-detached', 'media-error', 'toggle-play'],
  setup(props, { emit }) {
    const videoElement = ref(null)
    const videoFrame = ref(null)
    const hasVideo = computed(() => Boolean(props.videoSrc || props.youtubeVideoId))

    function connectVideo() {
      if (videoElement.value) emit('media-ready', videoElement.value)
    }

    function toggleFullscreen() {
      const element = props.youtubeVideoId ? videoFrame.value : videoElement.value
      if (!element) return
      if (document.fullscreenElement) {
        document.exitFullscreen?.()
        return
      }
      element.requestFullscreen?.()
    }

    onMounted(connectVideo)

    watch(() => props.videoSrc, async (nextSource) => {
      if (props.youtubeVideoId) return
      const previousElement = videoElement.value
      if (previousElement) emit('media-detached', previousElement)
      await nextTick()
      if (!nextSource) return
      videoElement.value?.load()
      connectVideo()
    })

    onBeforeUnmount(() => {
      if (videoElement.value) emit('media-detached', videoElement.value)
    })

    return {
      videoElement,
      videoFrame,
      hasVideo,
      toggleFullscreen,
    }
  },
}
</script>

<style scoped>
.media-workspace {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.media-workspace.is-audio {
  display: flex;
}

.media-workspace.is-video {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(22rem, 1fr);
}

.media-stage,
.transcript-pane,
.transcript-body {
  min-width: 0;
  min-height: 0;
}

.media-stage {
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  padding: 0;
  gap: 0;
  overflow: hidden;
  border-right: 1px solid var(--border-subtle);
}

.video-frame {
  position: relative;
  display: grid;
  place-items: center;
  align-self: center;
  justify-self: center;
  width: calc(100% - 1.5rem);
  max-height: calc(100% - 1.5rem);
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: #07101d;
}

.video-element {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #07101d;
  cursor: pointer;
}

.video-element:focus-visible,
.youtube-video-hit-area:focus-visible,
.stage-play-button:focus-visible {
  outline: 3px solid #67e0dc;
  outline-offset: 3px;
}

.youtube-video-hit-area {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  color: var(--color-text-inverse);
  background: transparent;
  cursor: pointer;
}

.stage-play-button {
  position: absolute;
  inset: 50% auto auto 50%;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  min-height: 46px;
  padding: 0 1rem;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  color: #ffffff;
  background: rgba(7, 16, 29, 0.82);
  backdrop-filter: blur(12px);
  font: inherit;
  font-size: 0.82rem;
  font-weight: 750;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.28);
}

.stage-play-button:hover {
  background: #13a8a8;
  border-color: #55d7d4;
}

.stage-play-icon {
  width: 0;
  height: 0;
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  border-left: 9px solid currentColor;
}

.segment-counter {
  position: absolute;
  z-index: 3;
  top: 0.75rem;
  right: 0.75rem;
  padding: 0.36rem 0.52rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #d9fdfb;
  background: rgba(7, 16, 29, 0.62);
  backdrop-filter: blur(10px);
  font-family: "JetBrains Mono", "SF Mono", Consolas, monospace;
  font-size: 0.66rem;
  font-weight: 750;
  pointer-events: none;
}

.transcript-pane {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.is-audio .transcript-pane,
.is-audio .transcript-body {
  flex: 1 1 auto;
  width: 100%;
}

.transcript-body {
  flex: 1 1 auto;
  overflow: hidden;
}

.transcript-body :deep(.subtitle-container),
.transcript-body :deep(.subtitle-display) {
  width: 100%;
  height: 100%;
  max-width: none;
}

.transcript-body :deep(.subtitle-display) {
  padding: var(--space-6) var(--space-3) 10rem;
}

/* 纯音频时字幕列限制阅读宽度并居中，宽屏上不会拉成一长条 */
.is-audio .transcript-body :deep(.subtitle-display) {
  padding-inline: max(var(--space-6), calc((100% - 960px) / 2));
}

@media (max-width: 980px) {
  .media-workspace.is-video {
    grid-template-columns: minmax(0, 3fr) minmax(18rem, 2fr);
  }

}

@media (max-width: 768px) {
  .media-workspace.is-video {
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(0, 1fr);
  }

  .media-stage {
    grid-template-rows: auto;
    gap: 0;
    padding: 0;
    border-right: 0;
    border-bottom: 1px solid var(--border-subtle);
  }

  .video-frame {
    width: 100%;
    height: auto;
    max-height: none;
    margin: 0;
    border-width: 0;
    border-radius: 0;
    box-shadow: none;
  }

  .segment-counter {
    top: 0.5rem;
    right: 0.5rem;
  }

  .transcript-body :deep(.subtitle-display) {
    padding-top: var(--space-2);
  }

  /* 跟读栏和播放条浮在字幕上，底部渐隐到纸色，两条之间不再露出半截字幕 */
  .is-video .transcript-pane {
    position: relative;
  }

  .is-video .transcript-pane::after {
    content: "";
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    height: 9.5rem;
    background: linear-gradient(to bottom, transparent, var(--surface-canvas) 45%);
    pointer-events: none;
  }

  .stage-play-button {
    min-height: 40px;
    padding: 0 0.8rem;
    font-size: 0.74rem;
  }

}

@media (max-height: 540px) and (orientation: landscape) {
  .media-stage {
    grid-template-rows: minmax(0, 1fr);
    padding: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .stage-play-button {
    transition: none;
  }
}
</style>
